import React, { useState, useEffect } from 'react';
import { View, ScrollView, ActivityIndicator, useWindowDimensions, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ThemedText } from '@/components/themed-text';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useProviderProfile, useUpdateProvider } from '@/hooks/use-provider';
import { USER_ROLE } from '@/types/user-role';
import { isProviderRole } from '@/utils/user-role';
import { ControlledInput } from '@/components/forms/controlled-input';
import { useForm } from 'react-hook-form';
import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/api-error';

interface EditProfileFormData {
  username: string;
  // Provider fields
  title?: string;
  bio?: string;
  businessPhone?: string;
  address?: string;
  // Client fields
  phone?: string;
  clientAddress?: string;
}

interface EditProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditProfileModal({ open, onOpenChange, onSuccess }: EditProfileModalProps) {
  const { user, fetchProfile } = useAuthStore();
  const { addToast } = useToastStore();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isProvider = isProviderRole(user?.role);
  const isClient = user?.role === USER_ROLE.CLIENT;
  /**
   * Com um profissional em foco, o que o modal edita é o perfil dele (a API
   * resolve pelo header). O nome de usuário é da conta logada e ficaria
   * gravando no dono, então some do formulário.
   */
  const isActing = useActingProviderStore((s) => !!s.acting);

  const { data: providerData } = useProviderProfile();
  const updateProvider = useUpdateProvider();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<EditProfileFormData>({
    defaultValues: {
      username: '',
      title: '',
      bio: '',
      businessPhone: '',
      address: '',
      phone: '',
      clientAddress: '',
    },
  });

  // Atualizar valores quando o modal abrir
  useEffect(() => {
    if (open) {
      reset({
        username: user?.username || '',
        // Provider
        title: providerData?.title || '',
        bio: providerData?.bio || '',
        businessPhone: providerData?.businessPhone || '',
        address: providerData?.address || '',
        // Client
        phone: user?.clientProfile?.phone || '',
        clientAddress: user?.clientProfile?.address || '',
      });
    }
  }, [open, user, providerData, reset]);

  const onSubmit = async (data: EditProfileFormData) => {
    setIsSubmitting(true);

    try {
      // Atualizar nome de usuário (só a própria conta)
      if (!isActing && data.username !== user?.username) {
        await api.patch('/user/me', { username: data.username });
      }

      // Atualizar perfil do provider
      if (isProvider) {
        await updateProvider.mutateAsync({
          title: data.title,
          bio: data.bio,
          businessPhone: data.businessPhone,
          address: data.address,
        });
      }

      // Atualizar perfil do cliente
      if (isClient && user?.clientProfile) {
        await api.patch(`/client/${user.clientProfile.id}`, {
          phone: data.phone,
          address: data.clientAddress,
        });
      }

      // Recarregar o perfil do usuário
      await fetchProfile();

      addToast('Perfil atualizado com sucesso!', 'success');
      onSuccess?.();
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      addToast(getApiErrorMessage(error, 'Erro ao atualizar perfil.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  // Layout responsivo: tela cheia no mobile, modal maior no desktop
  const contentClassName = isMobile ? 'w-full h-full rounded-none' : 'w-[95%] min-w-[600px]';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={contentClassName}>
        {/* Botão de fechar (X) */}
        <Pressable
          onPress={handleClose}
          className="absolute right-4 top-4 z-10 rounded-full p-2 hover:bg-neutral-100"
        >
          <Ionicons className="text-neutral-500" name="close" size={24} />
        </Pressable>

        <DialogHeader className="pr-10">
          <DialogTitle>
            <View className="flex-row items-center gap-3">
              <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                <Ionicons
                  className="text-primary dark:text-white"
                  name="person-outline"
                  size={20}
                />
              </View>
              <ThemedText className="font-bold text-xl">Editar Perfil</ThemedText>
            </View>
          </DialogTitle>
          <DialogDescription>
            <ThemedText className="text-sm text-foreground/60">
              Atualize suas informações pessoais
            </ThemedText>
          </DialogDescription>
        </DialogHeader>

        <ScrollView
          keyboardShouldPersistTaps="handled"
          className={isMobile ? 'flex-1' : 'max-h-[50vh]'}
          showsVerticalScrollIndicator={false}
        >
          <View className="gap-4 py-4">
            {/* Nome — da conta logada, some com um profissional em foco */}
            {!isActing && (
              <ControlledInput
                control={control}
                name="username"
                label="Nome Completo"
                placeholder="Seu nome completo"
              />
            )}

            {/* Campos do Provider */}
            {isProvider && (
              <>
                <ControlledInput
                  control={control}
                  name="title"
                  label="Nome do Negócio"
                  placeholder="Nome do seu negócio"
                />

                <ControlledInput
                  control={control}
                  name="businessPhone"
                  label="Telefone Comercial"
                  placeholder="(99) 99999-9999"
                  keyboardType="phone-pad"
                  maskType="phone"
                />

                <ControlledInput
                  control={control}
                  name="address"
                  label="Endereço"
                  placeholder="Seu endereço comercial"
                />

                <ControlledInput
                  control={control}
                  name="bio"
                  label="Sobre o Negócio"
                  placeholder="Descreva seu negócio brevemente..."
                  type="textarea"
                />
              </>
            )}

            {/* Campos do Cliente */}
            {isClient && (
              <>
                <ControlledInput
                  control={control}
                  name="phone"
                  label="Telefone"
                  placeholder="(99) 99999-9999"
                  keyboardType="phone-pad"
                  maskType="phone"
                />

                <ControlledInput
                  control={control}
                  name="clientAddress"
                  label="Endereço"
                  placeholder="Seu endereço"
                />
              </>
            )}
          </View>
        </ScrollView>

        <DialogFooter className="mt-4">
          <Button variant="outline" onPress={handleClose} disabled={isSubmitting}>
            <ThemedText>Cancelar</ThemedText>
          </Button>
          <Button onPress={handleSubmit(onSubmit)} disabled={isSubmitting || !isDirty}>
            {isSubmitting ? (
              <View className="flex-row items-center gap-2">
                <ActivityIndicator size="small" color="white" />
                <ThemedText className="text-white">Salvando...</ThemedText>
              </View>
            ) : (
              <ThemedText className="text-white">Salvar Alterações</ThemedText>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
